import { useState } from 'react';
import { Link } from 'react-router-dom';
import { BackToTopPill } from "@/components/BackToTopPill";
import { useTranslation } from 'react-i18next';
import RoleGuard from '@/components/auth/RoleGuard';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { RefreshCw, Loader2, BellRing, ArrowUpRight } from 'lucide-react';
import { usePageTitle } from '@/contexts/PageTitleContext';
import { PageActionBar } from '@/components/layout/PageActionBar';
import { useRole } from '@/hooks/use-role';
import { useStockIntelligence } from '@/features/stock-alerts/hooks/useStockIntelligence';
import {
  StockAlertsSummaryCards,
  type SummaryFilterKey,
} from '@/features/stock-alerts/components/StockAlertsSummaryCards';
import { ReorderRecommendations } from '@/features/stock-alerts/components/ReorderRecommendations';
import { FastMoversSection } from '@/features/stock-alerts/components/FastMoversSection';
import { OosAlertSettingsSheet } from '@/features/stock-alerts/components/OosAlertSettingsSheet';

const StockAlertsContent = () => {
  const { t } = useTranslation('inventory');
  const { isAdmin } = useRole();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('recommendations');
  const [summaryFilter, setSummaryFilter] = useState<SummaryFilterKey>(null);
  const { data, isLoading, isFetching, refetch, hasLocation } =
    useStockIntelligence();

  usePageTitle(t('stockAlerts.title'), t('stockAlerts.subtitle'));

  // Clicking a summary card toggles its filter and (since it only applies to
  // the recommendations table) jumps to that tab so the effect is visible.
  const handleToggleFilter = (key: Exclude<SummaryFilterKey, null>) => {
    setSummaryFilter((prev) => (prev === key ? null : key));
    setActiveTab('recommendations');
  };

  return (
    <div className="flex flex-col">
      <main className="flex-1 w-full py-6 px-4 sm:px-6 lg:px-8 space-y-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <PageActionBar
            ariaLabel="Stock alerts actions"
            left={
              <TabsList className="h-8">
                <TabsTrigger value="recommendations" className="h-6 text-xs">{t('stockAlerts.recommendations')}</TabsTrigger>
                <TabsTrigger value="fast-movers" className="h-6 text-xs">{t('stockAlerts.fastMovers')}</TabsTrigger>
              </TabsList>
            }
            right={
              <>
                <Button
                  asChild
                  variant="ghost"
                  size="sm"
                  className="h-8 px-3 text-xs gap-1.5 text-muted-foreground"
                >
                  <Link to="/reporting/blanks">
                    {t('stockAlerts.blankBuyingView', 'Blank buying view')}
                    <ArrowUpRight className="h-3.5 w-3.5" />
                  </Link>
                </Button>
                {isAdmin() && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 px-3 text-xs gap-1.5"
                    onClick={() => setSettingsOpen(true)}
                  >
                    <BellRing className="h-3.5 w-3.5" />
                    Alert settings
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 px-3 text-xs gap-1.5"
                  onClick={() => refetch()}
                  disabled={isFetching}
                >
                  {isFetching ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <RefreshCw className="h-3.5 w-3.5" />
                  )}
                  {t('stockAlerts.refresh')}
                </Button>
              </>
            }
          />

          <div className="mt-6 space-y-6">
            {!hasLocation && (
              <p className="text-sm text-muted-foreground italic">
                {t('stockAlerts.locationHint')}
              </p>
            )}

            <StockAlertsSummaryCards
              summary={data?.summary}
              isLoading={isLoading}
              activeFilter={summaryFilter}
              onToggleFilter={handleToggleFilter}
            />

            <TabsContent value="recommendations" className="mt-0">
              <ReorderRecommendations
                items={data?.reorderRecommendations}
                isLoading={isLoading}
                hasLocation={hasLocation}
                summaryFilter={summaryFilter}
                onClearSummaryFilter={() => setSummaryFilter(null)}
              />
            </TabsContent>
            <TabsContent value="fast-movers" className="mt-0">
              <FastMoversSection
                items={data?.fastMovers}
                summary={data?.fastMoversSummary}
                isLoading={isLoading}
                hasLocation={hasLocation}
              />
            </TabsContent>
          </div>
        </Tabs>
      </main>

      {isAdmin() && (
        <OosAlertSettingsSheet open={settingsOpen} onOpenChange={setSettingsOpen} />
      )}
      <BackToTopPill />
    </div>
  );
};

const StockSuggestions = () => (
  <RoleGuard allowedRoles={['Super Admin', 'Admin', 'Warehouse Coordinator']}>
    <StockAlertsContent />
  </RoleGuard>
);

export default StockSuggestions;
